import { useEffect, useRef, type FC } from 'react';
import { 
    Phone, 
    PhoneOff, 
    Trash2, 
    Wifi, 
    WifiOff 
} from 'lucide-react';
import type { UseRealtimeTranscriptReturn } from '../../hooks/useRealtimeTranscript';

interface LiveTranscriptProps {
  callId?: string;
  transcriptHook: UseRealtimeTranscriptReturn;
}

const LiveTranscript: FC<LiveTranscriptProps> = ({ callId, transcriptHook }) => {
  const {
    messages,
    isConnected,
    isTyping,
    callEvents,
    joinRoom,
    clearMessages,
  } = transcriptHook;

  const chatWindowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // Join room if callId is provided
  useEffect(() => {
    if (callId && isConnected && joinRoom) {
      joinRoom(callId);
    }
  }, [callId, isConnected, joinRoom]);

  const formatTimestamp = (date: Date) => {
    try {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
        return 'Invalid time';
      }
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid time';
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <Phone className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Live Transcript</h3>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Connection Status */}
          <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${
            isConnected 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {isConnected ? (
              <>
                <Wifi className="w-4 h-4" />
                <span>Connected</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4" />
                <span>Disconnected</span>
              </>
            )}
          </div>

          {/* Clear Button */}
          <button
            onClick={clearMessages}
            disabled={!clearMessages}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Clear transcript"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Transcript Messages */}
      <div 
        ref={chatWindowRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
        style={{ minHeight: '400px', maxHeight: '600px' }}
      >
        {messages.length === 0 && callEvents.length === 0 && (
          <div className="flex items-center justify-center h-full text-gray-400">
            <div className="text-center">
              <Phone className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Waiting for call to start...</p>
              <p className="text-xs mt-1">Transcript will appear here in real-time</p>
            </div>
          </div>
        )}

        {/* Render Call Events */}
        {callEvents && callEvents.length > 0 && callEvents.map((event, index) => {
          if (!event) return null;
          
          return (
            <div key={`event-${index}`} className="flex justify-center my-4">
              <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                event.type === 'call_started' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {event.type === 'call_started' ? (
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4" />
                    <span>Call Started</span>
                    {event.fromNumber && <span className="opacity-70">({event.fromNumber})</span>}
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <PhoneOff className="w-4 h-4" />
                    <span>Call Ended</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Render Messages */}
        {messages && messages.length > 0 && messages.map((message, index) => {
          // Skip rendering if message or required properties are undefined/null
          if (!message || 
              typeof message.text !== 'string' || 
              !message.speaker || 
              !message.timestamp) {
            console.warn('Skipping invalid message:', message);
            return null;
          }
          
          return (
            <div
              key={`msg-${index}-${message.timestamp.getTime()}`}
              className={`flex ${message.speaker === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-3 rounded-lg ${
                  message.speaker === 'user'
                    ? 'bg-blue-500 text-white rounded-br-sm'
                    : 'bg-white border border-gray-200 text-gray-900 rounded-bl-sm'
                }`}
              >
                <div className="text-xs opacity-70 mb-1">
                  {message.speaker === 'user' ? 'Patient' : 'AI Assistant'}
                </div>
                <div className="text-sm leading-relaxed whitespace-pre-wrap">
                  {message.text}
                </div>
                <div className={`text-xs mt-1 ${
                  message.speaker === 'user' ? 'text-blue-100' : 'text-gray-400'
                }`}>
                  {formatTimestamp(message.timestamp)}
                </div>
              </div>
            </div>
          );
        })}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 px-4 py-3 rounded-lg rounded-bl-sm">
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
                <span className="text-xs text-gray-500 italic">Assistant is speaking...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Info */}
      {callId && (
        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500">
            Room: <span className="font-mono text-gray-700">call_{callId}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default LiveTranscript;
